
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChatService } from "../services/chat/chat.services";
import { AudioService } from "../services/text-to-audio/texttoaudio.service";
import { TrackingService } from "../services/tracking/tracking.service";
import { ExerciseQuestionsService } from "../services/exercise-questions/exercise-questions.service";
import useExerciseChatStore from "../utils/exerciseChatStore";
import { useQuestionsStore } from "../utils/questionStore";
import { useExerciseStore } from "../utils/exerciseStore";
import { useUserStore } from "../utils/userStore";
import { useStepsStore } from "../utils/stepStore";
import { useLayoutContext } from "../layouts/general-layout";
import { parseQuestionSteps } from "../utils/parseSteps";
import { RouterConstant } from "../routers/index";

// Types
export interface Message {
  text: string;
  isBot: boolean;
  role: "system" | "user" | "assistant";
  static_helper_text?: boolean;
  timestamp: Date;
}

const getFormattedSolutionTitle = (t: any, letter: string): string => {
    if (!letter) return "";
    return `**${t("sectionSolution")} ${letter}**\n\n`;
};

export const useChat = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { ROUTERS_PATH } = RouterConstant;
    const { t } = useLayoutContext();
    
    // Stores
    // Optmization: Don't destructure `messages` directly to avoid re-rendering on every store update
    // We only access messages via getState() in async handlers.
    const addMessage = useExerciseChatStore((state) => state.addMessage);
    const clearMessages = useExerciseChatStore((state) => state.clearMessages);
    
    const selectedExerciseId = useExerciseStore((s) => s.selectedExerciseId);
    const exerciseFilename = useExerciseStore((s) => s.selectedExerciseName);
    const { markQuestionInProcess, addCompletedQuestion, getQuestionHistory } = useQuestionsStore();
    const selectedQuestionLetter = useQuestionsStore((s) => s.selectedQuestionLetter);
    const { user_attributes, updateAttribute } = useUserStore();
    const stepsStore = useStepsStore();

    // Local State
    const [displayMessages, setDisplayMessages] = useState<Message[]>([]);
    const [inputMessage, setInputMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    
    // Exercise-Specific State
    // Ideally this would be in a separate hook useExerciseFlow, but kept here for now as requested
    const [allStepsSolution, setAllStepsSolution] = useState(false);
    const [solutionResponse, setSolutionResponse] = useState<string>("");
    const [solutionSteps, setSolutionSteps] = useState<string[]>([]);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [showQuestionList, setShowQuestionList] = useState(false);
    const [isDirectMeClicked, setIsDirectMeClicked] = useState(false);
    const [showConfirmationButtons, setShowConfirmationButtons] = useState(false);
    const [allStepsCompleted, setAllStepsCompleted] = useState(false);
    const [isSolutionByImage, setIsSolutionByImage] = useState(false);
    const [nextStep, setNextStep] = useState(false);
    const [hasPlayedSolutionAudio, setHasPlayedSolutionAudio] = useState(false);

    // Services
    const chatServiceInstance = useRef(new ChatService()).current;
    const trackingServiceInstance = useRef(new TrackingService()).current;
    const audioServiceInstance = useRef(new AudioService()).current;
    const exerciseService = useRef(new ExerciseQuestionsService()).current;

    // Refs
    const playedAudioKeysRef = useRef<Set<string>>(new Set());
    const shouldShowExplanationAfterRender = useRef(false);

    // Navigation Context
    const { api, payload } = location.state || {};
    const selectedQuestion = payload?.selected_question || "";
    const isSelectedQuestion = !!payload?.selected_question;
    const selectedQuestionId = location.state?.selectedQuestionId || "";
    const stepContents = location.state?.stepContents || "";
    const steps = location.state?.steps || [];
    const selectedStepIndex = location.state?.stepIndex || 0;
    const navigate_from = location.state?.from;
    const previewPageState = location.state?.previewPageState;
    const isFromSelfTestAnswerPreview = previewPageState?.from === ROUTERS_PATH.SELFTESTANSWERPREVIEW;

    // Derived State
    const questionHistory = getQuestionHistory(selectedExerciseId);
    const currentQuestions = questionHistory?.current || [];
    const completedQuestions = questionHistory?.completed || [];
    
    const isAllQuestionsCompletedNow = useMemo(() => {
        return currentQuestions.length > 0 && currentQuestions.every((id: any) => completedQuestions?.includes(id));
    }, [currentQuestions, completedQuestions]);

    // --- Helpers ---
    
    // Audio Helper
    const playAudio = useCallback(async (key: string, replayable: boolean = false) => {
        if (!replayable && playedAudioKeysRef.current.has(key)) return;
        
        try {
            audioServiceInstance.stopAudio();
            // Await a small microtask to allow UI to settle if needed
            await new Promise(resolve => setTimeout(resolve, 50));
            await audioServiceInstance.handleTextToAudioByKey(key);
            if (!replayable) playedAudioKeysRef.current.add(key);
        } catch (error: any) {
             if (error.name === 'AbortError') {
                 // Retry once if aborted
                setTimeout(() => {
                    audioServiceInstance.handleTextToAudioByKey(key);
                }, 100);
            } else {
                console.error('Audio error:', error);
            }
        }
    }, []);

    // Message Helper
    const createMessage = useCallback((text: string, role: "system" | "user" | "assistant", isBot: boolean, static_helper_text?: boolean): Message => {
        return {
            text,
            isBot,
            role,
            timestamp: new Date(),
            static_helper_text
        };
    }, []);

    const addDisplayMessage = useCallback((message: Message) => {
        setDisplayMessages((prev) => [...prev, message]);
    }, []);

    const addAndDisplayMessage = useCallback((text: string, role: "system" | "user" | "assistant", isBot: boolean, static_helper_text?: boolean) => {
        const msg = createMessage(text, role, isBot, static_helper_text);
        addMessage(msg); // Add to global store
        addDisplayMessage(msg); // Add to local display
    }, [addMessage, addDisplayMessage, createMessage]);

    const removeLastDisplayMessage = useCallback(() => {
        setDisplayMessages((prev) => prev.length > 0 ? prev.slice(0, -1) : prev);
    }, []);

    // Tracking Helper
    const handleTracking = useCallback((event_name: string, userInput?: string) => {
        const properties: any = {
            source: "chatpage",
            selected_exercise: exerciseFilename || "",
            selected_question: selectedQuestionLetter || "",
            selected_step_name: `${t("STEP")} ${currentStepIndex + 1}`,
        };

        if (navigate_from === "choose-exercise") {      
            properties.selected_exercise = "";
            properties.selected_question = "";
            properties.selected_step_name = "";
        }

        if (event_name === "click_free_chat_question" && userInput) {
            const words = userInput.trim().split(/\s+/);
            properties.user_input = words.length > 500 ? words.slice(0, 500).join(" ") : userInput;
        }

        if (event_name === "click_solve_exercise_from_image" || api === "TalkWithChat_by_image") {
            properties.source = "chatpage_by_image";
            properties.selected_exercise = "";
            properties.selected_question = "";
            properties.selected_step_name = "";       
        }

        trackingServiceInstance.trackEvent(event_name, properties);
    }, [exerciseFilename, selectedQuestionLetter, currentStepIndex, navigate_from, api, t]);

    // --- Core Logic ---

    const handleChat = useCallback(async (userMessage: string) => {
        try {
            const lastDisplayMessage = displayMessages[displayMessages.length - 1];
            let shouldReAddFurtherExplanation = false;
            
            if (
                lastDisplayMessage &&
                lastDisplayMessage.text === t("furtherExplanation") &&
                lastDisplayMessage.role === "system"
            ) {
                removeLastDisplayMessage(); 
                shouldReAddFurtherExplanation = true;
            }

            setIsLoading(true);
            
            // Optimization: Read store state only when needed
            const currentMessages = useExerciseChatStore.getState().messages; 
            const recentMessages = currentMessages.slice(-10);
            
            // Add User Message
            addAndDisplayMessage(userMessage, "user", false);

            const requestPayload: any = {
                message: userMessage,
                exercise_id: payload?.exercise_id ?? null,
                selected_question: payload?.selected_question ?? null,
                selected_step: !allStepsSolution ? solutionSteps[currentStepIndex] : null,
            };
            
            if (recentMessages && recentMessages.length > 0) {
                requestPayload.history = recentMessages.map((msg) => ({
                    content: msg.text,
                    role: msg.role,
                }));
            }
            
            handleTracking("click_free_chat_question", userMessage);
            const response = await chatServiceInstance.TALKWITHCHAT(requestPayload);
            
            if (response) {
                 addAndDisplayMessage(response, "assistant", true);
            }

            if (shouldReAddFurtherExplanation) {
                // Re-add helper text
                const systemMessage = createMessage(t("furtherExplanation"), "system", true, true);
                addDisplayMessage(systemMessage);
            }
        } catch (error) {
            console.error(error);
             const errorMessage = createMessage(t("communicationError"), "system", false);
            addDisplayMessage(errorMessage); 
        } finally {
            setIsLoading(false);
        }
    }, [displayMessages, payload, solutionSteps, currentStepIndex, allStepsSolution, t, addAndDisplayMessage, addDisplayMessage, removeLastDisplayMessage, handleTracking, createMessage]);

    // Api Handlers Strategy Map
    const apiHandlers = useMemo(() => ({
        "TalkWithChat": async (data: any) => {
            await handleChat(data.message);
        },
        "TalkWithChat_by_image": async (data: any) => {
            setIsSolutionByImage(true);
            const systemMessage = createMessage(t("basedOnLanguageModel"), "system", true, true);
            addDisplayMessage(systemMessage);
            
            handleTracking("click_solve_exercise_from_image");
            const response = await chatServiceInstance.TALKWITHCHATBYIMAGE(data.image);        
            addAndDisplayMessage(response.solution_content, "assistant", true);
        },
        "TalkWithChat_by_image_specific_question": async (data: any) => {
             setIsSolutionByImage(true);
             const systemMessage = createMessage(t("basedOnLanguageModel"), "system", true, true);
             addDisplayMessage(systemMessage);
             
             addAndDisplayMessage(`${data.question}`, "user", false);
     
             handleTracking("click_solve_specific_image_question");
             const response = await chatServiceInstance.SOLVE_SPECIFIC_IMAGE_QUESTION(data);        
             addAndDisplayMessage(response.solution_content, "assistant", true);
        },
        "all_steps_solution": async (data: any) => {
            setAllStepsSolution(true);
            const solutionStepsMessage = createMessage(`${t("stepsToSolve")} ${selectedQuestionLetter}`, "system", true, true);
            addDisplayMessage(solutionStepsMessage);

            const response = await chatServiceInstance.GETQUESTIONSTEPS(data);
            const parsed = parseQuestionSteps(response.response_content);
            const chatStepContents = parsed.map(
                (s, index) => `<b>${t("STEP")} ${index + 1}: ${s.title}</b> ${s.content}`
            );
            setSolutionResponse(chatStepContents.join("<br/>"));
            addAndDisplayMessage(chatStepContents.join("<br/>"), "assistant", true);

            const sectionSolutionMessage = createMessage(`${t("sectionSolution")} ${selectedQuestionLetter}`, "system", true, true);
            addDisplayMessage(sectionSolutionMessage);
            
            const requestData = {
                exercise_id: payload.exercise_id,
                solution_steps: chatStepContents.join("<br/>"),
                selected_question: selectedQuestion,
                all_steps_solution: true,
            };
            const solutions_response = await chatServiceInstance.SOLUTIONBYSTEPS(requestData);
            const title = getFormattedSolutionTitle(t, selectedQuestionLetter);
            const formattedSolution = `${title}${solutions_response.solution_content}`;
            setSolutionResponse(formattedSolution);
            addAndDisplayMessage(formattedSolution, "assistant", true);
            
            const systemMessage = createMessage(t("furtherExplanation"), "system", true, true);
            addDisplayMessage(systemMessage);

            if (selectedQuestionId) {
                markQuestionInProcess(selectedExerciseId, selectedQuestionId);
            }
        },
        "solution_by_steps": async (data: any) => {
             setSolutionSteps(stepContents);
             setCurrentStepIndex(selectedStepIndex);

             addAndDisplayMessage(stepContents[selectedStepIndex], "assistant", true);

             const systemMessage: Message = createMessage(t("stepSolveInNotebook"), "system", true, true);
             addDisplayMessage(systemMessage);

             if (selectedQuestionId) {
                 markQuestionInProcess(selectedExerciseId, selectedQuestionId);
             } 
        }
    }), [addAndDisplayMessage, addDisplayMessage, createMessage, handleChat, handleTracking, markQuestionInProcess, payload, selectedQuestion, selectedQuestionId, selectedExerciseId, selectedQuestionLetter, selectedStepIndex, stepContents, t]);


    const handleChatWithPayload = useCallback(async (apiEndpoint: string, data: any) => {
        try {
            setIsLoading(true);
            const handler = apiHandlers[apiEndpoint as keyof typeof apiHandlers];
            if (handler) {
                await handler(data);
            } else {
                console.warn(`No handler found for apiEndpoint: ${apiEndpoint}`);
            }
        } catch (error) {
            console.error("API Error:", error);
            const errorMessage = createMessage(t("communicationError"), "system", false);
            addDisplayMessage(errorMessage); 
        } finally {
            setIsLoading(false);
        }
    }, [apiHandlers, addDisplayMessage, createMessage, t]);


    const handleExplainSolution = async (isDetailsExplained: boolean) => {
        setIsLoading(true);    
        const currentMessages = useExerciseChatStore.getState().messages; 
        const recentMessages = currentMessages.slice(-10);
        
        const isDetails = typeof isDetailsExplained === 'boolean' ? isDetailsExplained : false;
        if (!isDetails) {
            handleTracking("click_show_me_tip");
        }
        const requestData: any = {
            exercise_id: payload.exercise_id,
            prev_response: solutionResponse ? solutionResponse : null,
            selected_question: selectedQuestion,
            selected_question_id: selectedQuestionId ? Number(selectedQuestionId) : null,
            selected_step: !allStepsSolution ? solutionSteps[currentStepIndex] : null,
            all_steps_solution: allStepsSolution,
            is_details_explain: isDetails,
            chat_history: [
                ...recentMessages.map((msg) => ({
                    content: msg.text,
                    role: msg.role,
                })),
            ],
        };
        if (currentStepIndex === 0) {
            requestData.is_first_step = true;
        }
        let response = await chatServiceInstance.EXPLAINSTEPSOLUTION(requestData);
        if (response) {      
             const formattedContent = (!isDetails && selectedQuestionLetter)
                 ? `**${t("hintForSection")} ${selectedQuestionLetter}**\n\n${response.solution_content}`
                 : response.solution_content;
             addAndDisplayMessage(formattedContent, "assistant", true);
            
            if(showConfirmationButtons || allStepsSolution && !showQuestionList )
                setIsDirectMeClicked(false);
            else
                setIsDirectMeClicked(true);
        }     
        setIsLoading(false);    
    };

    const handlefurtherExplanation = async (answer: "Yes" | "No") => {
        let isAllStepsCompleted = false;
        if (!allStepsSolution) {      
            isAllStepsCompleted = currentStepIndex + 1 >= solutionSteps.length && solutionSteps.length > 0;
            if (isAllStepsCompleted) {
                if (selectedQuestionId) {
                    addCompletedQuestion(selectedExerciseId, selectedQuestionId);
                }              
            }
            setAllStepsCompleted(isAllStepsCompleted);
            setShowConfirmationButtons(false);
        } else {
            if (selectedQuestionId) {
                addCompletedQuestion(selectedExerciseId, selectedQuestionId);
            }
        }    
        
        if (answer !== "Yes") {
            handleTracking("click_show_explanation_details_no");
            removeLastDisplayMessage();      
            if (!isAllStepsCompleted ) {
                playAudio("chatpage_voice_exaplin4", true);
            } else if (isAllStepsCompleted && !isAllQuestionsCompletedNow) {
                playAudio("chatpage_voice_exaplin5");
            } else if (isAllQuestionsCompletedNow && isAllStepsCompleted) {
                playAudio("chatpage_voice_exaplin6");
            }
        } else {
            handleTracking("click_show_explanation_details_yes");
            await handleExplainSolution(true);      
        }

        if (isAllStepsCompleted) {
            const chart = useQuestionsStore.getState().getChartByQuestionId(selectedQuestionId);  
            if (chart) {
                const systemMessage = createMessage(t("scketchSolution"), "system", true, true);
                addDisplayMessage(systemMessage);
                
                const chartMessage = createMessage(`<img src="${chart}" alt="chart" class="chat-chart-image" />`, "assistant", true);
                addMessage(chartMessage);
                addDisplayMessage(chartMessage);
            }
        }

        if (allStepsSolution) {
            setShowQuestionList(true);
        }
        setNextStep(true);
    };

    const handleSolutionBySteps = async () => {
        handleTracking("click_show_me_solution");
        const stepContent = solutionSteps[currentStepIndex];

        try {
            if (!hasPlayedSolutionAudio) {
                setIsLoading(true);
                playAudio("chatpage_before_solution_display", true);        
                setHasPlayedSolutionAudio(true);
                setIsLoading(false);
            } else {
                setIsLoading(true);
                const currentMessages = useExerciseChatStore.getState().messages; 
                const recentMessages = currentMessages.slice(-10);
                
                const requestData: any = {
                    exercise_id: payload.exercise_id,
                    solution_steps: stepContent,    
                    selected_question: selectedQuestion,
                    selected_question_id: payload.selected_question_id,
                    selected_step_id: steps[currentStepIndex].id,
                    all_steps_solution: false,
                    chat_history: [
                        ...recentMessages.map((msg) => ({
                            content: msg.text,
                            role: msg.role,
                        })),
                    ],
                };
                if (currentStepIndex === 0) {
                    requestData.is_first_step = true;
                }
                const response = await chatServiceInstance.SOLUTIONBYSTEPS(requestData);
                if (response) {        
                    const title = getFormattedSolutionTitle(t, selectedQuestionLetter);
                    const formattedSolution = `${title}${response.solution_content}`;
                    setSolutionResponse(formattedSolution);
                    if (!user_attributes?.has_exercise_history) {
                        updateAttribute("has_exercise_history", true);
                    }      
                    addAndDisplayMessage(formattedSolution, "assistant", true);

                    shouldShowExplanationAfterRender.current = true;       
                }
            }
        } catch (error) {
            console.error("Error fetching solution:", error);
            const errorMessage = createMessage(t("communicationError"), "system", false);
            addDisplayMessage(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNextStep = () => {
        let stepIndex = currentStepIndex;
        if (stepIndex >= solutionSteps.length) {
            stepIndex = 0;
            setCurrentStepIndex(1);
        } else {
            stepIndex = currentStepIndex + 1;
            setCurrentStepIndex(stepIndex);
            setHasPlayedSolutionAudio(false);
        }
        
        const stepContent = solutionSteps[stepIndex];
        if (stepIndex !== selectedStepIndex) {
            const stepContentMessage = createMessage(stepContent, "system", true);
            addDisplayMessage(stepContentMessage);
            
            const systemMessage = createMessage(t("stepSolveInNotebook"), "system", true, true);
            addDisplayMessage(systemMessage);
            setNextStep(false);
        }
    };

    const handleUpsertExerciseHistory = async (options: { index?: number; is_completed: boolean; }) => {
        const { index, is_completed } = options || {};
    
        let resolvedStepId: number | undefined;
        if (index) {
            resolvedStepId = steps[index].id;
        } else {
            resolvedStepId = steps[currentStepIndex].id;
        }
    
        if (!selectedExerciseId || !selectedQuestionId || !resolvedStepId) {
            console.warn("Missing required identifiers: exercise, question, or step");
            return;
        }
    
        try {
            await exerciseService.upsertExerciseHistory({
                selected_exercise_id: selectedExerciseId,
                selected_question_id: selectedQuestionId,
                selected_step_id: resolvedStepId,
                is_completed_steps: is_completed,
                is_completed_question: allStepsCompleted ? true : false,
            });
    
            markQuestionInProcess(selectedExerciseId, selectedQuestionId);
    
            if (is_completed) {
                stepsStore.addCompletedStep(selectedQuestionId, resolvedStepId);
            } else {
                stepsStore.markStepInProcess(selectedQuestionId, resolvedStepId);
            }
        } catch (error) {
            console.error("Error updating exercise history", error);
        }
    };
    
    // Cleanup & Routing
    const handleBackClick = useCallback(() => {
        clearMessages();
        if (navigate_from  === "choose-exercise") {
            navigate(ROUTERS_PATH.CHOOSEEXERCISE);
        } else if (navigate_from === ROUTERS_PATH.SELFTESTANSWERPREVIEW) {
            navigate(ROUTERS_PATH.SELFTESTANSWERPREVIEW);
        } else if (navigate_from === ROUTERS_PATH.QUESTIONSTEPS) {
            navigate(ROUTERS_PATH.QUESTIONSTEPS, { state: location.state });
        } else if (navigate_from === "display-exercise") {
            navigate(ROUTERS_PATH.DISPLAYEXERCISE, {
                state: { from: "chat", payload },
            });
        } else {
            navigate(ROUTERS_PATH.EXERCISEQUESTIONS);
        }
    }, [clearMessages, navigate, navigate_from, ROUTERS_PATH, location.state, payload]);

    const handleMessageRendered = () => { 
        if (shouldShowExplanationAfterRender.current) {
            const systemMessage = createMessage(t("furtherExplanation"), "system", true, true);
            addDisplayMessage(systemMessage);
            setShowConfirmationButtons(true);
            shouldShowExplanationAfterRender.current = false;
        }

        if (navigate_from === "exercise-questions") return; 

        if (stepContents !== "") {
            playAudio("chatpage_voice_exaplin1");
        }
        if (solutionResponse !== "") {
            playAudio("chatpage_voice_exaplin3");
        }
        if (isDirectMeClicked) {
            playAudio("chatpage_voice_exaplin2"); 
        } 
        if (nextStep && !allStepsCompleted ) {
            playAudio("chatpage_voice_exaplin4", true);
        }   
        if (allStepsCompleted && !isAllQuestionsCompletedNow) {
            playAudio("chatpage_voice_exaplin5");
        }
        if (isAllQuestionsCompletedNow && allStepsCompleted) {
            playAudio("chatpage_voice_exaplin6");
        }
    };

    // Effects
    useEffect(() => {
        return () => {
            clearMessages();
            setDisplayMessages([]);
        };
    }, [clearMessages]);

    useEffect(() => {
        if (api && payload) {
            handleChatWithPayload(api, payload);
        }
    }, []);

    return {
        // State
        displayMessages,
        inputMessage,
        setInputMessage,
        isLoading,
        allStepsSolution,
        showQuestionList,
        isDirectMeClicked,
        showConfirmationButtons,
        allStepsCompleted,
        isAllQuestionsCompletedNow,
        isSolutionByImage,
        nextStep,
        selectedQuestion,
        selectedQuestionId,
        currentStepIndex,
        isSelectedQuestion,
        isFromSelfTestAnswerPreview,
        
        // Handlers
        handleChat,
        handleExplainSolution,
        handlefurtherExplanation,
        handleSolutionBySteps,
        handleNextStep,
        handleUpsertExerciseHistory,
        handleMessageRendered,
        handleTracking,
        handleBackClick,
        
        // Navigation / other
        navigate,
        ROUTERS_PATH,
        navigate_from,
        location,
        payload
    };
};
